import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { YStack, Spinner, Label } from '@fliisi/pollen'
import { useLanguage } from '@fliisi/i18n'

const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL

type Status = 'loading' | 'success' | 'error'

function useQueryParams() {
  if (Platform.OS !== 'web') return { email: null, topic: null, token: null }
  const params = new URLSearchParams(window.location.search)
  return {
    email: params.get('email'),
    topic: params.get('topic'),
    token: params.get('token'),
  }
}

const TOPIC_LABELS_FI: Record<string, string> = {
  notifications: 'Ilmoitukset',
  'store-updates': 'Kaupan päivitykset',
  offers: 'Tarjoukset',
  marketing: 'Markkinointi',
}

const TOPIC_LABELS_EN: Record<string, string> = {
  notifications: 'Notifications',
  'store-updates': 'Store updates',
  offers: 'Offers',
  marketing: 'Marketing',
}

export default function UnsubscribeScreen() {
  const { t, language } = useLanguage()
  const { email, topic, token } = useQueryParams()
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!email || !topic || !token) {
      setStatus('error')
      setErrorMsg(language === 'fi' ? 'Virheellinen linkki' : 'Invalid link')
      return
    }

    const unsubscribe = async () => {
      try {
        const url = `${SUPABASE_URL}/functions/v1/unsubscribe?email=${encodeURIComponent(email)}&topic=${encodeURIComponent(topic)}&token=${encodeURIComponent(token)}`
        const res = await fetch(url)
        const data = await res.json()

        if (data.ok) {
          setStatus('success')
        } else {
          setStatus('error')
          setErrorMsg(data.message || (language === 'fi' ? 'Jokin meni pieleen' : 'Something went wrong'))
        }
      } catch {
        setStatus('error')
        setErrorMsg(language === 'fi' ? 'Jokin meni pieleen' : 'Something went wrong')
      }
    }

    unsubscribe()
  }, [email, topic, token])

  const labels = language === 'fi' ? TOPIC_LABELS_FI : TOPIC_LABELS_EN
  const topicLabel = topic ? (labels[topic] ?? topic) : ''

  return (
    <YStack flex={1} justifyContent="center" alignItems="center" backgroundColor="$background" padding="$lg">
      <YStack
        backgroundColor="$card"
        borderRadius="$lg"
        padding="$xl"
        maxWidth={420}
        width="100%"
        alignItems="center"
        gap="$md"
      >
        <Label
          size="xl"
          tone="primary"
          weight="bold"
        >
          fliisi
        </Label>

        {status === 'loading' && (
          <>
            <Spinner size="large" />
            <Label size="md" tone="muted">
              {language === 'fi' ? 'Käsitellään...' : 'Processing...'}
            </Label>
          </>
        )}

        {status === 'success' && (
          <>
            <Label size="lg" weight="bold">
              {language === 'fi' ? 'Tilaus peruutettu ✓' : 'Unsubscribed ✓'}
            </Label>
            <Label size="md" tone="muted" textAlign="center">
              {language === 'fi'
                ? `Olet peruuttanut tilauksesi: ${topicLabel}`
                : `You have been unsubscribed from: ${topicLabel}`}
            </Label>
          </>
        )}

        {status === 'error' && (
          <>
            <Label size="lg" weight="bold">
              {language === 'fi' ? 'Virhe' : 'Error'}
            </Label>
            <Label size="md" tone="muted" textAlign="center">
              {errorMsg}
            </Label>
          </>
        )}
      </YStack>
    </YStack>
  )
}
